import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { formatCurrency, siteConfig } from "../Datas/productData";
import "../css/privacy.scss";

function Delivery() {
  const email = siteConfig.contact?.email;
  const standardRate = formatCurrency(siteConfig.shipping?.standardRate);
  const freeThreshold = formatCurrency(siteConfig.shipping?.freeThreshold);

  useEffect(() => {
    document.title = `Shipping — ${siteConfig.brandName}`;
  }, []);

  return (
    <main className="policy-page policy-page--shipping" id="main-content">
      <header className="policy-hero">
        <p className="policy-eyebrow">Shipping + delivery</p>
        <h1>Happy mail is headed your way.</h1>
        <p>
          Every {siteConfig.brandName} order is packed with care. Use this guide
          to choose the delivery option that fits your moment.
        </p>
      </header>

      <div className="shipping-options" aria-label="Shipping options">
        <article>
          <span>Everyday</span>
          <h2>Standard</h2>
          <strong>4–7 business days</strong>
          <p>{standardRate} tracked delivery. Free on orders of {freeThreshold} or more.</p>
        </article>
        <article className="shipping-options__featured">
          <span>Need it sooner?</span>
          <h2>Express</h2>
          <strong>2–3 business days</strong>
          <p>Available for eligible destinations and in-stock products.</p>
        </article>
        <article>
          <span>Far and wide</span>
          <h2>International</h2>
          <strong>7–21 business days</strong>
          <p>Timing varies by destination and local customs processing.</p>
        </article>
      </div>

      <div className="policy-layout">
        <aside className="policy-nav" aria-label="Shipping guide sections">
          <p>On this page</p>
          <a href="#shipping-processing">Processing</a>
          <a href="#shipping-tracking">Tracking</a>
          <a href="#shipping-international">International orders</a>
          <a href="#shipping-delays">Delays and address changes</a>
          <a href="#shipping-damage">Delivery issues</a>
        </aside>

        <article className="policy-content">
          <section className="policy-lead" id="shipping-processing">
            <h2>Processing time</h2>
            <p>
              In-stock orders typically leave our studio within 1–3 business
              days, excluding holidays. Personalized and pre-order items show
              their own lead times on the product page. Orders containing
              different lead times normally ship together when all items are ready.
            </p>
          </section>

          <section>
            <h2>Rates and delivery estimates</h2>
            <p>
              Available services, prices and the most current estimate appear
              at checkout after you enter a delivery address. Estimates begin
              after processing and are not guaranteed by the carrier.
            </p>
          </section>

          <section id="shipping-tracking">
            <h2>Tracking your order</h2>
            <p>
              When tracking is available, we email a link as soon as the parcel
              is scanned by the carrier. It can take up to 48 hours for the first
              update to appear. Check spam or promotions folders if you do not
              see the dispatch email.
            </p>
          </section>

          <section id="shipping-international">
            <h2>International orders</h2>
            <p>
              International customers are responsible for import duties, taxes
              and brokerage fees charged by their country unless checkout says
              otherwise. Customs can extend delivery times. We cannot mark an
              order as a gift or lower its declared value.
            </p>
          </section>

          <section id="shipping-delays">
            <h2>Address changes and delays</h2>
            <p>
              Contact us immediately if an address needs correcting. We can
              update it only before fulfillment begins and cannot guarantee a
              carrier reroute. Weather, holidays, customs and service disruptions
              may cause delays outside our control.
            </p>
          </section>

          <section id="shipping-damage">
            <h2>Missing or damaged deliveries</h2>
            <p>
              If tracking says delivered, first check safe locations, household
              members and neighbors, then allow one additional business day.
              For damage, missing contents or a parcel that has stopped moving,
              contact us with your order number and photos where relevant.
            </p>
            <p>
              Also see our <Link to="/returns">returns and damaged-item guide</Link>.
            </p>
          </section>

          <section className="policy-contact">
            <p className="policy-eyebrow">Where is my happy mail?</p>
            <h2>We will help track it down.</h2>
            <p>
              Send your order number through our <Link to="/contact">contact page</Link>
              {email && <> or email <a href={`mailto:${email}`}>{email}</a></>}.
            </p>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Delivery;
